import { Slide, useMediaQuery, useTheme } from '@mui/material'
import type { TransitionProps } from '@mui/material/transitions'
import React from 'react'

import {
  BottomActionBar,
  type BottomActionBarProps,
} from '../../surfaces/bottom-action-bar'

import {
  StyledDialog,
  StyledDialogActions,
  StyledDialogContent,
} from './Dialog.styled'
import { DialogTitle } from './dialog-title/DialogTitle'

export interface DialogProps {
  title: string
  content?: JSX.Element
  bottomActions?: BottomActionBarProps
  children?: React.ReactNode
  isOpen: boolean
  variant?: 'default' | 'fullScreenOnMobile' | 'bottomSheet'
  withGutters?: boolean
  onClose?: () => void
  'data-testid'?: string
}

const UpTransition = React.forwardRef(
  (
    props: TransitionProps & { children: React.ReactElement<unknown> },
    ref: React.Ref<unknown>,
  ) => {
    return (
      <Slide direction="up" ref={ref} {...props}>
        {props.children}
      </Slide>
    )
  },
)

export const Dialog = ({
  title,
  content,
  bottomActions,
  isOpen = false,
  variant = 'default',
  withGutters = true,
  onClose,
  'data-testid': dataTestId,
}: DialogProps): JSX.Element => {
  const theme = useTheme()
  const fullScreen = variant === 'fullScreenOnMobile'
  const isBottomSheet = variant === 'bottomSheet'
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <StyledDialog
      open={isOpen}
      onClose={onClose}
      fullScreen={fullScreen}
      TransitionComponent={isBottomSheet && isMobile ? UpTransition : undefined}
      $variant={variant}
      data-testid={dataTestId}
      fullWidth
    >
      <DialogTitle onClose={onClose}>{title}</DialogTitle>

      {content && (
        <StyledDialogContent withGutters={withGutters}>
          {content}
        </StyledDialogContent>
      )}

      {bottomActions && (
        <StyledDialogActions>
          <BottomActionBar {...bottomActions} />
        </StyledDialogActions>
      )}
    </StyledDialog>
  )
}
